import PropTypes from "prop-types";
import { useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { IconMail, IconSend } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { useShowWhenSet } from "./Decision";
import { outcomeOfStatus } from "./decisions";
import { AGENT_STATUSES, statusLabel } from "./format";
import { partnerName, travellerLabel } from "./workflow";

// Changing a case's status. Two explicit buttons for every status, "Save
// without emailing" and "Save and email the customer": no tick box that
// switches itself on for some statuses (QA nadia-09). The dismiss button
// says "Keep as it is", never "Cancel" next to "Set to Cancelled" (nadia-10).
// Approved and Refused go through "Record the decision" (one way, nadia-07).
// A traveller alone can still be moved, as the classic request page allowed;
// so can a draft's travellers, one at a time (the case as a whole can't move
// before the customer submits).
//
// onSave({ scope, status, notify, reason }) answers true (saved), false (the
// agent stopped at the unpaid question) or { failed, reason }. scope is
// "case" or one traveller's request id.
export default function StatusDialog({ caseData, initial, initialScope, onClose, onSave, onDecide }) {
  const draft = caseData.status === "incomplete";
  const many = caseData.travellers.length > 1;
  const partner = partnerName(caseData);
  const email = caseData.customer?.email;
  const [scope, setScope] = useState(() => (draft ? initialScope || caseData.travellers[0]?.request_id : initialScope || "case"));
  const traveller = scope === "case" ? null : caseData.travellers.find((t) => t.request_id === scope) || caseData.travellers[0];
  const current = traveller ? traveller.status : caseData.status;
  const startFrom = (now) => (now === "pending" ? "under_review" : now);
  const [status, setStatus] = useState(initial || startFrom(current));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(null);
  const [failed, setFailed] = useState(null);
  const failure = useShowWhenSet(failed);
  const same = status === current;
  const choices = traveller ? ["incomplete", ...AGENT_STATUSES] : AGENT_STATUSES;
  // The whole application to Approved or Refused: the decision's own flow.
  const decides = !traveller && !!outcomeOfStatus(status);
  const others = traveller ? caseData.travellers.length - 1 : 0;

  const save = async (notify) => {
    setSaving(notify ? "email" : "quiet");
    setFailed(null);
    const result = await onSave({ scope: traveller ? traveller.request_id : "case", status, notify, reason: notify && !traveller ? reason.trim() : "" });
    setSaving(null);
    if (result === true) onClose();
    // false: the agent stopped at the unpaid warning; nothing to say.
    else if (result && result.failed) setFailed({ notify, reason: result.reason });
  };

  const who = traveller ? travellerLabel(caseData, traveller) : null;
  const moves = traveller
    ? `Only ${who} moves to ${statusLabel(status)}${others ? `; the other ${others === 1 ? "traveller keeps their" : `${others} travellers keep their`} status` : ""}.`
    : many
      ? `All ${caseData.travellers.length} travellers move to ${statusLabel(status)}.`
      : `The application moves to ${statusLabel(status)}.`;

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="case-status-title">
      <DialogTitle id="case-status-title">Change the status</DialogTitle>
      <DialogContent>
        {draft ? (
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            The customer hasn’t submitted this application yet, so the case can’t move as a whole. You can change one traveller at a time.
          </Typography>
        ) : null}
        {many || draft ? (
          <RadioGroup
            value={String(traveller ? traveller.request_id : "case")}
            onChange={(e) => {
              const next = e.target.value === "case" ? "case" : Number(e.target.value);
              setScope(next);
              const t = next === "case" ? null : caseData.travellers.find((x) => x.request_id === next);
              setStatus(startFrom(t ? t.status : caseData.status));
              setFailed(null);
            }}
            aria-label="Who changes"
            sx={{ mb: 1, "& .MuiFormControlLabel-label": { pl: 0.5 } }}
          >
            {!draft ? (
              <FormControlLabel value="case" control={<Radio />} label={many ? `The whole application (${caseData.travellers.length} travellers)` : "The application"} />
            ) : null}
            {caseData.travellers.map((t) => (
              <FormControlLabel
                key={t.request_id}
                value={String(t.request_id)}
                control={<Radio />}
                label={`Only ${travellerLabel(caseData, t)}, now ${statusLabel(t.status)}`}
              />
            ))}
          </RadioGroup>
        ) : null}
        <TextField
          select
          fullWidth
          label="New status"
          value={status}
          autoFocus
          onChange={(e) => {
            setStatus(e.target.value);
            setFailed(null);
          }}
          sx={{ mt: 1 }}
          helperText={same ? `It is already ${statusLabel(current)}. Pick a different status to continue.` : " "}
        >
          {choices.map((s) => (
            <MenuItem key={s} value={s}>
              {statusLabel(s)}
              {s === current ? " (now)" : ""}
            </MenuItem>
          ))}
        </TextField>

        {decides ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            {statusLabel(status)} is saved with the consulate’s decision: what the letter says{outcomeOfStatus(status) === "approved" ? ", the visa" : ""} and
            whether the customer is emailed, in one place.
          </Alert>
        ) : (
          <>
            {!traveller ? (
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={partner ? "Message for the traveller (optional)" : "Message in the email (optional)"}
                helperText="Only sent with “Save and email”."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                inputProps={{ maxLength: 1000 }}
              />
            ) : null}
            {/* What happens, once a different status is picked: before that,
                "already Draft" and "moves to Draft" contradicted each other (nadia-21). */}
            {!same && <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "action.hover", border: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">
                {moves} The timeline records the change.
              </Typography>
              {partner ? (
                <Typography variant="body2" mt={0.5}>
                  <b>{partner}</b> receives the new status either way. With “Save and tell {partner}”, they tell the traveller
                  {reason.trim() ? ", with your message" : ""}.
                </Typography>
              ) : (
                <Typography variant="body2" mt={0.5}>
                  With “Save and email the customer”, {email ? <b>{email}</b> : "the customer"} gets: “Your application is now: <b>{statusLabel(status)}</b>.”
                  {reason.trim() && !traveller ? " Then your message." : ""} With “Save without emailing”, nobody is emailed.
                </Typography>
              )}
            </Box>}
          </>
        )}

        {/* A failed save says what did not happen, here, not in a passing toast. */}
        <Box aria-live="assertive" ref={failure}>
          {failed ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {failed.notify
                ? `The status wasn’t changed and ${partner ? `${partner} wasn’t told` : "no email was sent"}. Try again.`
                : "The status wasn’t changed. Try again."}
              {failed.reason ? ` ${failed.reason}` : ""}
            </Alert>
          ) : null}
        </Box>
      </DialogContent>
      {/* Long labels ("Save and tell <partner>") wrap on a phone. */}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap", "& > :not(style) ~ :not(style)": { ml: 0 } }}>
        <ActionButton variant="text" size="medium" onClick={onClose} disabled={!!saving}>
          Keep as it is
        </ActionButton>
        {decides ? (
          <ActionButton variant="contained" size="medium" disabled={same} onClick={() => onDecide(outcomeOfStatus(status))}>
            Next: record the decision
          </ActionButton>
        ) : (
          <>
            <ActionButton
              variant="outlined"
              size="medium"
              sx={{ whiteSpace: "normal", maxWidth: "100%", textAlign: "center" }}
              disabled={same || saving === "email"}
              busyLabel="Saving…"
              onClick={() => save(false)}
            >
              {partner ? "Save without telling the traveller" : "Save without emailing"}
            </ActionButton>
            <ActionButton
              variant="contained"
              size="medium"
              startIcon={partner ? <IconSend size={18} /> : <IconMail size={18} />}
              sx={{ whiteSpace: "normal", maxWidth: "100%", textAlign: "center" }}
              disabled={same || saving === "quiet" || (!partner && !email)}
              busyLabel="Saving…"
              onClick={() => save(true)}
            >
              {partner ? `Save and tell ${partner}` : "Save and email the customer"}
            </ActionButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

StatusDialog.propTypes = {
  caseData: PropTypes.object.isRequired,
  initial: PropTypes.string,
  initialScope: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDecide: PropTypes.func.isRequired,
};
